import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RemunerationPolicyData } from '@shared/types';

import api from '../../../../api.js';

export interface RemunerationPolicyState {
  data: RemunerationPolicyData | null;
  isLoading: boolean;
  error: string | null;
  selectedYear: number;
  availableYears: number[];
}

const currentYear = new Date().getFullYear();
const defaultYears = Array.from({ length: 12 }, (_, i) => currentYear - i);

const initialState: RemunerationPolicyState = {
  data: null,
  isLoading: false,
  error: null,
  selectedYear: currentYear - 1,
  availableYears: defaultYears,
};

export const getRemunerationPolicy = async (companyId: string | number, year: number | null) => {
  const url = year
    ? `/api/v2/remuneration-policy/${companyId}?year=${year}`
    : `/api/v2/remuneration-policy/${companyId}`;
  const response = await api.get(url);
  return response.data;
};

export const fetchRemunerationPolicy = createAsyncThunk(
  'remunerationPolicy/fetch',
  async ({ companyId, year }: { companyId: string; year: number | null }) => {
    const response = await getRemunerationPolicy(companyId, year);
    return response;
  },
);

const RemunerationPolicySlice = createSlice({
  name: 'companyRemunerationPolicy',
  initialState,
  reducers: {
    resetRemunerationPolicy: (state) => {
      state.data = null;
      state.isLoading = false;
      state.error = null;
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRemunerationPolicy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRemunerationPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.data ?? null;
        state.error = null;
      })
      .addCase(fetchRemunerationPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Failed to load remuneration policy';
      });
  },
});

export const { resetRemunerationPolicy, setSelectedYear } = RemunerationPolicySlice.actions;

export default RemunerationPolicySlice.reducer;
